import { Outlet, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useExpensablesDataTable } from 'generated/resources/datatable.graphql'
import { ExpensePageFinanceCustomCategoryFragment } from 'generated/resources/graphql'
import { some, values } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { useQuickFiltersContext } from 'modules/expenses/components/expenses/PageContents/QuickFiltersContext'
import { Currency, ExpensePageSubproject } from 'modules/expenses/hooks/useExpensesListPage'
import { Group } from 'modules/expenses/hooks/useGroup'
import { PageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { datesToLocalString, isMyExpensesSection } from 'modules/expenses/lib/common'
import { canAnyExpensableAction } from 'modules/expenses/lib/expensables/availableActions'
import { isDraftGroup } from 'modules/expenses/lib/groups/group'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'

import { Actions } from './_Actions'
import { Amount } from './_Amount'
import { Card } from './_Card'
import { Category } from './_Category'
import { Date } from './_Date'
import { Detail } from './_Detail'
import { DocumentType } from './_DocumentType'
import { EmptyState } from './_EmptyState'
import { ExpensesInsights } from './_ExpensesInsights'
import { allFilters } from './_Filters'
import { Header } from './_Header'
import { Owner } from './_Owner'
import { PaymentMethod } from './_PaymentMethod'
import { SpendingAlertPill } from './_SpendingAlertPill'
import { Status } from './_Status'
import type { Expensable } from './types'
import { useActions } from './useActions'

interface Props {
  companyId: number
  employeeIds?: number[]
  dateFilters?: PageDateFilters
  setPageDateFilters?: (startDate: Date, endDate: Date) => void
  group?: Group
  categories: ExpensePageFinanceCustomCategoryFragment[]
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  currencies: Currency[]
  subprojects: ExpensePageSubproject[]
  subcategories: ExpensePageFinanceCustomCategoryFragment[]
  back: { href: string }
  hasReportOnBehalfFeature: boolean
  canSeeTransactions: boolean
  canRestartApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  onAddUsers?: () => void
  addUsersDisabled?: boolean
  recommendationsEnabled?: boolean
}

export const ExpensesDataTable = ({
  companyId,
  group,
  categories,
  dateFilters,
  setPageDateFilters,
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  currencies,
  subprojects,
  employeeIds,
  subcategories,
  back,
  hasReportOnBehalfFeature,
  canSeeTransactions,
  canRestartApprovalFlow,
  canSetExpensableToPaid,
  onAddUsers,
  addUsersDisabled,
  recommendationsEnabled,
}: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const { section } = context

  const { filters, clearFilters } = useQuickFiltersContext()
  const hasFilters = some(values(filters), (value) => !!value)

  const { from, to } = datesToLocalString(dateFilters?.startDate, dateFilters?.endDate)
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const { featureAvailabilities, data } = useExpenses()

  const customizationFields = data?.expenses?.customizationFields ?? []

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const { actions, Modals } = useActions({
    currentPayrollPeriodOpen,
    availablePayrollCategories,
  })

  const source = useExpensablesDataTable(
    [
      {
        identifier: 'owner',
        title: i18n.t('expenses_v2.list.header.owner'),
        kind: ProjectionKind.Custom,
        template: '{{ owner.access.fullName }}',
        render: ({ owner }) => {
          return <Owner owner={owner} />
        },
      },
      {
        identifier: 'status',
        title: i18n.t('expenses_v2.list.header.status'),
        kind: ProjectionKind.Custom,
        template: '{{ status | i18n: "expenses.status" }}',
        render: ({ status }) => {
          return <Status status={status} />
        },
        sortBy: 'status',
      },
      {
        identifier: 'effectiveOn',
        title: i18n.t('expenses_v2.list.header.date'),
        kind: ProjectionKind.Custom,
        template: '{{ effectiveOn | date: "%Y-%m-%d" }}',
        render: ({ effectiveOn }) => {
          return <Date date={effectiveOn} />
        },
        sortBy: 'effectiveOn',
      },
      {
        identifier: 'createdAt',
        title: i18n.t('expenses_v2.list.header.created_at'),
        hidden: true,
        kind: ProjectionKind.Custom,
        template: '{{ createdAt | date: "%Y-%m-%d" }}',
        render: ({ createdAt }) => {
          return <Date date={createdAt} />
        },
        sortBy: 'createdAt',
      },
      {
        identifier: 'documentType',
        title: i18n.t('expenses_v2.list.header.document_type'),
        hidden: true,
        kind: ProjectionKind.Custom,
        template:
          '{% if expense %} {{expense.documentType | i18n: "expenses.customization.document_type" }} {% endif %}',
        render: ({ expense }) => {
          return expense?.documentType ? (
            <DocumentType
              documentType={expense?.documentType}
              customizationFields={customizationFields}
            />
          ) : null
        },
      },
      {
        identifier: 'paymentMethod',
        title: i18n.t('expenses_v2.list.header.payment_method'),
        kind: ProjectionKind.Custom,
        hidden: true,
        template:
          '{% if expense && expense.creationType === "manual" %} {{expense.paymentMethod | i18n: "expenses.customization.payment_method" }} {% else %} {{expense.card.last4}} {% endif %}',
        render: ({ expense }) => {
          if (!expense) return null
          const { creationType, paymentMethod, card } = expense
          if (creationType === 'manual') {
            return paymentMethod ? (
              <PaymentMethod
                paymentMethod={paymentMethod}
                customizationFields={customizationFields}
              />
            ) : null
          }
          return card ? <Card card={card} /> : null
        },
      },

      {
        identifier: 'amount',
        title: i18n.t('expenses_v2.list.header.amount'),
        kind: ProjectionKind.Custom,
        template: '{{ amount | plus: 0.0 | divided_by: 100}} {{currency}}',
        render: ({ amount, currency }) => {
          return <Amount amount={Number(amount)} currency={currency} />
        },
        width: 150,
        sortBy: 'amount',
      },
      {
        identifier: 'reimbursableAmount',
        title: i18n.t('expenses_v2.list.header.reimbursable_amount'),
        kind: ProjectionKind.Custom,
        template: '{{ reimbursableAmount | plus: 0.0 | divided_by: 100}} {{reimbursableCurrency}}',
        hidden: true,
        render: ({ reimbursableAmount, reimbursableCurrency }) => {
          return (
            <Amount
              amount={Number(reimbursableAmount)}
              currency={reimbursableCurrency ? reimbursableCurrency : ''}
            />
          )
        },
        width: 230,
        sortBy: 'reimbursableAmount',
      },
      {
        identifier: 'category',
        title: i18n.t('expenses_v2.list.header.category'),
        kind: ProjectionKind.Custom,
        template:
          '{% if expense %} {{expense.category.name}} {% else %} {{mileage.category.name}} {% endif %}',
        render: ({ expense, mileage, perDiem }) => {
          return <Category expensable={expense || mileage || perDiem} />
        },
      },
      {
        identifier: 'spending_alerts',
        title: i18n.t('expenses_v2.list.header.spending_alerts'),
        kind: ProjectionKind.Custom,
        template:
          '{% assign alerts = spendingAlertsTarget | concat: spendingAlertsSource %}{% assign failing_alerts = alerts | where: "status", "failed" %}{% assign unique_alerts = failing_alerts | uniq: "type" %}{% for alert in unique_alerts %}{{alert.type | i18n: "expenses.alerts"}}{% unless forloop.last %}, {% endunless %}{% endfor %}',
        render: (expensable: Expensable) => {
          return (
            <SpendingAlertPill
              expensableAmount={expensable.amount}
              alerts={[...expensable.spendingAlertsTarget, ...expensable.spendingAlertsSource]}
              locale={locale}
            />
          )
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 50,
        render: (expensable: Expensable) => {
          return (
            <Actions
              expensable={expensable}
              {...actions}
              canRestartApprovalFlow={canRestartApprovalFlow}
              canSetExpensableToPaid={canSetExpensableToPaid}
            />
          )
        },
      },
      {
        identifier: 'detail',
        kind: ProjectionKind.Decorative,
        width: 70,
        render: ({ id, currency, amount, effectiveOn, owner }) => {
          if (!id) return null
          return (
            <Detail
              id={id}
              effectiveOn={effectiveOn}
              currency={currency}
              amount={Number(amount)}
              owner={owner}
            />
          )
        },
      },
      {
        identifier: 'reference',
        title: i18n.t('expenses_v2.list.header.internal_reference'),
        kind: ProjectionKind.Path,
        path: 'internalReference',
        hidden: true,
      },
    ],
    {
      variables: {
        companyId: companyId.toString(),
        groupIds: group ? [group.id] : null,
        from: from,
        to: to,
        employeeIds: employeeIds,
        search: debouncedSearch,
        includeManualDrafts: isMyExpensesSection(section),
      },
    }
  )

  const clearQuickFilters = () => {
    if (hasFilters) clearFilters()
  }

  const datatableFilters = allFilters({
    expensesContext: section ?? 'company',
    withinGroup: !!group,
    categories,
    currencies,
    subprojects,
    subcategories,
    initialFilterValues: { ...filters },
    clearQuickFilters,
    includeReportedByFilter: hasReportOnBehalfFeature,
    canCreateExpensesForOthers: data?.expenses?._policies?.expenseCreateExpensesForOthers?.can,
    recommendationsEnabled,
  })

  return (
    <DataTable.Provider
      persistFilters={!hasFilters}
      enableClearFilters
      filters={datatableFilters}
      pagination={{
        kind: 'cursor',
        itemsPerPage: 25,
        infiniteScroll: true,
      }}
      source={source}
      sort={{ path: 'effectiveOn', direction: 'asc' }}
    >
      <Modals />
      <Outlet context={{ ...context, back, canSeeTransactions, group }} />
      <Box gap="s16">
        <Header
          group={group}
          search={search}
          onSearch={onSearch}
          onAddUsers={onAddUsers}
          {...actions}
          canRestartApprovalFlow={data?.expenses?._policies?.expenseRestartApprovalFlow.can}
          canSetExpensableToPaid={data?.expenses?._policies?.expenseSetToPaid.can}
          dateFilters={dateFilters}
          setPageDateFilters={setPageDateFilters}
          addUsersDisabled={addUsersDisabled}
        />
        <ExpensesInsights groupId={group?.id} employee={employee} dateFilters={dateFilters} />
        <DataTable.Table
          emptyState={
            isMyExpensesSection(section) && group && isDraftGroup(group) ? (
              <EmptyState
                canCreateExpenses={data.expenses._policies.expenseCreateExpenses.can}
                canUsePerDiems={featureAvailabilities.canUsePerDiems}
              />
            ) : null
          }
          selectable={(expensable) =>
            canAnyExpensableAction(
              featureAvailabilities.expensableGroups,
              expensable,
              featureAvailabilities.reimburseThroughPayroll,
              featureAvailabilities.reimburseThroughSepa,
              data?.expenses?._policies?.expenseRestartApprovalFlow.can,
              data?.expenses?._policies?.expenseSetToPaid.can,
              section,
              employee
            )
          }
          autoWidth
        />
      </Box>
    </DataTable.Provider>
  )
}
